import { useRememberDocument } from '@/contexts/DocumentWorkspace';
import { useIncomingDocument } from '@/contexts/DocumentWorkspace';
import { useState, useCallback, useRef, useEffect } from 'react';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import {
  Upload, Crop as CropIcon, Maximize2,
  Loader2, Download, Eye, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { PDFDocument } from 'pdf-lib';
import { pdfjsLib } from '@/lib/pdfWorker';
import { readPdfLibBytes, pdfIntakeErrorMessage } from '@/lib/pdfIntake';
import { parsePageRange, formatFileSize } from '@/lib/pdfOperations';
import { useFileDialog } from '@/hooks/useFileDialog';
import CloudSourceHint from '@/components/CloudSourceHint';
import PageCanvas from '@/components/PageCanvas';
import { ProGate } from '@/components/ProBadge';
import { usageCapApplies, hasReachedLimit, incrementUsage } from '../lib/webUsage';
import FreeTierBanner from '../components/FreeTierBanner';

// ─── constants ────────────────────────────────────────────────────────────────
const EDITOR_TARGET_W = 700;
const EDITOR_TARGET_H = 520;
const HANDLE_SIZE = 8;
const HANDLE_HIT = 8;
const MIN_RECT = 20;

const PAPER_SIZES_PT = {
  A4:          { w: 595, h: 842 },
  Letter:      { w: 612, h: 792 },
  A5:          { w: 420, h: 595 },
  'Half-Letter': { w: 396, h: 612 },
};

// ─── pure helpers ─────────────────────────────────────────────────────────────
function ptToUnit(pt, unit) {
  if (unit === 'in') return +(pt / 72).toFixed(3);
  if (unit === 'mm') return +(pt / 72 * 25.4).toFixed(2);
  return +pt.toFixed(1);
}

function unitToPt(val, unit) {
  const n = parseFloat(val);
  if (isNaN(n)) return 0;
  if (unit === 'in') return n * 72;
  if (unit === 'mm') return n / 25.4 * 72;
  return n;
}

function getHandlePositions(rect) {
  const { x, y, w, h } = rect;
  return [
    { id: 'nw', x,         y         },
    { id: 'n',  x: x+w/2,  y         },
    { id: 'ne', x: x+w,    y         },
    { id: 'e',  x: x+w,    y: y+h/2  },
    { id: 'se', x: x+w,    y: y+h    },
    { id: 's',  x: x+w/2,  y: y+h    },
    { id: 'sw', x,         y: y+h    },
    { id: 'w',  x,         y: y+h/2  },
  ];
}

function getHandleAt(mx, my, rect) {
  for (const h of getHandlePositions(rect)) {
    if (Math.abs(mx - h.x) <= HANDLE_HIT && Math.abs(my - h.y) <= HANDLE_HIT) return h.id;
  }
  return null;
}

const HANDLE_CURSORS = {
  nw: 'nwse-resize', n: 'ns-resize',   ne: 'nesw-resize',
  e:  'ew-resize',   se: 'nwse-resize', s:  'ns-resize',
  sw: 'nesw-resize', w:  'ew-resize',
};

function getCursorAt(mx, my, rect) {
  const h = getHandleAt(mx, my, rect);
  if (h) return HANDLE_CURSORS[h];
  if (mx >= rect.x && mx <= rect.x + rect.w && my >= rect.y && my <= rect.y + rect.h) return 'move';
  return 'crosshair';
}

function clampRect(rect, maxW, maxH) {
  const x = Math.max(0, Math.min(rect.x, maxW - MIN_RECT));
  const y = Math.max(0, Math.min(rect.y, maxH - MIN_RECT));
  const w = Math.max(MIN_RECT, Math.min(rect.w, maxW - x));
  const h = Math.max(MIN_RECT, Math.min(rect.h, maxH - y));
  return { x, y, w, h };
}

function applyResize(handle, rect, dx, dy, maxW, maxH) {
  let { x, y, w, h } = rect;
  if (handle.includes('w')) { x += dx; w -= dx; }
  if (handle.includes('e')) { w += dx; }
  if (handle.includes('n')) { y += dy; h -= dy; }
  if (handle.includes('s')) { h += dy; }
  return clampRect({ x, y, w, h }, maxW, maxH);
}

function drawOverlay(canvas, cropRect, displayW, displayH, dpr) {
  const ctx = canvas.getContext('2d');
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, displayW, displayH);

  // dim outside
  ctx.fillStyle = 'rgba(0,0,0,0.45)';
  ctx.fillRect(0, 0, displayW, displayH);

  // punch out crop area
  ctx.clearRect(cropRect.x, cropRect.y, cropRect.w, cropRect.h);

  // dashed border
  ctx.save();
  ctx.strokeStyle = 'rgba(255,255,255,0.9)';
  ctx.lineWidth = 1;
  ctx.setLineDash([5, 5]);
  ctx.strokeRect(cropRect.x + 0.5, cropRect.y + 0.5, cropRect.w - 1, cropRect.h - 1);
  ctx.restore();

  // handles
  ctx.fillStyle = 'white';
  ctx.strokeStyle = '#555';
  ctx.lineWidth = 1;
  ctx.setLineDash([]);
  for (const h of getHandlePositions(cropRect)) {
    ctx.fillRect(h.x - HANDLE_SIZE / 2, h.y - HANDLE_SIZE / 2, HANDLE_SIZE, HANDLE_SIZE);
    ctx.strokeRect(h.x - HANDLE_SIZE / 2, h.y - HANDLE_SIZE / 2, HANDLE_SIZE, HANDLE_SIZE);
  }
}

function cropRectToPdfBox(cropRect, scale, mediaBox) {
  return {
    x: mediaBox.x + cropRect.x / scale,
    y: mediaBox.y + mediaBox.height - (cropRect.y + cropRect.h) / scale,
    w: cropRect.w / scale,
    h: cropRect.h / scale,
  };
}

// ─── component ────────────────────────────────────────────────────────────────
export default function Crop() {
  const navigate = useNavigate();
  const rememberDocument = useRememberDocument();
  const { openFile } = useFileDialog();
  const overlayCanvasRef = useRef(null);
  const dragRef  = useRef(null); // { mode, handle, startX, startY, startRect }
  const scaleRef = useRef(1);   // mirrors scale state — readable in callbacks without stale closure

  // file
  const [pdfDoc,      setPdfDoc]      = useState(null);
  const [arrayBuffer, setArrayBuffer] = useState(null);
  const [fileName,    setFileName]    = useState('');
  const [fileSize,    setFileSize]    = useState(0);
  const [numPages,    setNumPages]    = useState(0);
  const [isDragging,  setIsDragging]  = useState(false);

  // crop editor
  const [cropRect,  setCropRect]  = useState(null); // px at current scale
  const [pageSize,  setPageSize]  = useState(null); // PDF points
  const [scale,     setScale]     = useState(1);    // display px per PDF pt
  const [currentPage,   setCurrentPage]   = useState(1);
  const [pageInputVal,  setPageInputVal]  = useState('1');

  // sidebar controls
  const [unit,         setUnit]         = useState('pt');
  const [equalMargin,  setEqualMargin]  = useState('');
  const [pageMode,     setPageMode]     = useState('current');
  const [customRange,  setCustomRange]  = useState('');
  const [autoMsg,      setAutoMsg]      = useState('');
  const [autoDetecting, setAutoDetecting] = useState(false);
  const [paperMsg,     setPaperMsg]     = useState('');

  // processing
  const [processing, setProcessing] = useState(false);
  const [progress,   setProgress]   = useState(0);
  const [error,      setError]      = useState(null);
  const [result,     setResult]     = useState(null);
  const [showFreeTierModal, setShowFreeTierModal] = useState(false);

  // ── helpers ──
  const pageDisplayW = pageSize ? pageSize.width  * scale : 0;
  const pageDisplayH = pageSize ? pageSize.height * scale : 0;

  // ── file loading ──
  const loadFile = useCallback(async (file) => {
    if (!file || file.type !== 'application/pdf' && !/\.pdf$/i.test(file.name)) return;
    if (pdfDoc) pdfDoc.destroy();
    setError(null);
    setResult(null);
    setCurrentPage(1);
    setPageInputVal('1');
    setFileName(file.name);
    setFileSize(file.size);
    try {
      // Decrypt owner-password PDFs (lossless) / reject truly locked ones
      // before pdf-lib ever sees the bytes — see src/lib/pdfIntake.js.
      const buf = await readPdfLibBytes(file);
      setArrayBuffer(buf);
      const doc = await pdfjsLib.getDocument({ data: buf.slice(0) }).promise;
      setPdfDoc(doc);
      rememberDocument(file);
      setNumPages(doc.numPages);

      const page = await doc.getPage(1);
      const vp = page.getViewport({ scale: 1 });
      const ps = { width: vp.width, height: vp.height };
      setPageSize(ps);

      const s = Math.min(EDITOR_TARGET_W / ps.width, EDITOR_TARGET_H / ps.height, 1.5);
      scaleRef.current = s;
      setScale(s);
      setCropRect({ x: 0, y: 0, w: ps.width * s, h: ps.height * s });
    } catch (err) {
      setError(pdfIntakeErrorMessage(err, 'crop it'));
    }
  }, [pdfDoc, rememberDocument]);

  useIncomingDocument(loadFile);

  const handleDrop = useCallback((e) => {
    e.preventDefault(); setIsDragging(false); loadFile(e.dataTransfer.files[0]);
  }, [loadFile]);
  const handleDragOver  = useCallback((e) => { e.preventDefault(); setIsDragging(true); }, []);
  const handleDragLeave = useCallback((e) => { e.preventDefault(); setIsDragging(false); }, []);
  const handleBrowse = useCallback(async () => {
    const result = await openFile('.pdf');
    if (result) {
      const file = new File([result.buffer], result.name, { type: 'application/pdf' });
      loadFile(file);
    }
  }, [openFile, loadFile]);

  const goToPage = useCallback(async (target) => {
    if (!pdfDoc || target < 1 || target > numPages) return;
    const page = await pdfDoc.getPage(target);
    const vp = page.getViewport({ scale: 1 });
    const ps = { width: vp.width, height: vp.height };
    const newScale = Math.min(EDITOR_TARGET_W / ps.width, EDITOR_TARGET_H / ps.height, 1.5);
    const oldScale = scaleRef.current;

    setCurrentPage(target);
    setPageInputVal(String(target));
    setPageSize(ps);
    scaleRef.current = newScale;
    setScale(newScale);

    setCropRect(prev => {
      if (!prev) return { x: 0, y: 0, w: ps.width * newScale, h: ps.height * newScale };
      // Convert from old-scale px → PDF pts → new-scale px
      const newRect = {
        x: (prev.x / oldScale) * newScale,
        y: (prev.y / oldScale) * newScale,
        w: (prev.w / oldScale) * newScale,
        h: (prev.h / oldScale) * newScale,
      };
      return clampRect(newRect, ps.width * newScale, ps.height * newScale);
    });
  }, [pdfDoc, numPages]);

  const autoDetectMargins = useCallback(async () => {
    if (!pdfDoc || !pageSize) return;
    setAutoDetecting(true);
    setAutoMsg('');
    try {
      const page = await pdfDoc.getPage(currentPage);
      const lowScale = 0.2;
      const vp = page.getViewport({ scale: lowScale });
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width  = Math.floor(vp.width);
      tempCanvas.height = Math.floor(vp.height);
      await page.render({ canvasContext: tempCanvas.getContext('2d'), viewport: vp }).promise;
      const { data, width, height } = tempCanvas.getContext('2d').getImageData(0, 0, tempCanvas.width, tempCanvas.height);

      const isWhite = (x, y) => {
        const i = (y * width + x) * 4;
        return data[i] >= 240 && data[i+1] >= 240 && data[i+2] >= 240;
      };

      let top = 0, bottom = height - 1, left = 0, right = width - 1;
      outer: for (let y = 0; y < height; y++) { for (let x = 0; x < width; x++) { if (!isWhite(x, y)) { top = y; break outer; } } }
      outer: for (let y = height - 1; y >= 0; y--) { for (let x = 0; x < width; x++) { if (!isWhite(x, y)) { bottom = y; break outer; } } }
      outer: for (let x = 0; x < width; x++) { for (let y = 0; y < height; y++) { if (!isWhite(x, y)) { left = x; break outer; } } }
      outer: for (let x = width - 1; x >= 0; x--) { for (let y = 0; y < height; y++) { if (!isWhite(x, y)) { right = x; break outer; } } }

      if (top >= bottom || left >= right) {
        setAutoMsg('No content detected — page may be blank.');
      } else {
        const ratio = scaleRef.current / lowScale;
        const padPx = 4 * scaleRef.current; // 4pt padding in display px
        const maxW = pageSize.width  * scaleRef.current;
        const maxH = pageSize.height * scaleRef.current;
        setEqualMargin('');
        setCropRect(clampRect({
          x: Math.max(0, left   * ratio - padPx),
          y: Math.max(0, top    * ratio - padPx),
          w: (right - left + 1) * ratio + padPx * 2,
          h: (bottom - top + 1) * ratio + padPx * 2,
        }, maxW, maxH));
      }
    } catch (err) {
      setAutoMsg(`Auto-detect failed: ${err.message}`);
    } finally {
      setAutoDetecting(false);
    }
  }, [pdfDoc, currentPage, pageSize]);

  const setPaperSize = useCallback((name) => {
    const size = PAPER_SIZES_PT[name];
    if (!size || !pageSize) return;
    const s = scaleRef.current;
    if (size.w > pageSize.width + 0.5 || size.h > pageSize.height + 0.5) {
      setPaperMsg(`Page is smaller than ${name} — cannot apply.`);
      return;
    }
    setPaperMsg('');
    // Center the paper size within the current page
    const x = ((pageSize.width  - size.w) / 2) * s;
    const y = ((pageSize.height - size.h) / 2) * s;
    setCropRect(clampRect({ x, y, w: size.w * s, h: size.h * s }, pageSize.width * s, pageSize.height * s));
  }, [pageSize]);

  const cropPDF = useCallback(async () => {
    if (!arrayBuffer || !cropRect || !pageSize) return;

    if (usageCapApplies() && hasReachedLimit()) {
      setShowFreeTierModal(true);
      return;
    }

    setProcessing(true);
    setProgress(0);
    setError(null);
    try {
      const pdfLibDoc = await PDFDocument.load(arrayBuffer);
      const pages = pdfLibDoc.getPages();
      const s = scaleRef.current;

      let targetIndices;
      if (pageMode === 'all') {
        targetIndices = pages.map((_, i) => i);
      } else if (pageMode === 'current') {
        targetIndices = [currentPage - 1];
      } else if (pageMode === 'custom') {
        targetIndices = parsePageRange(customRange, numPages).map(p => p - 1);
      } else if (pageMode === 'odd') {
        targetIndices = pages.map((_, i) => i).filter(i => i % 2 === 0); // 0-indexed odd pages = even indices
      } else {
        targetIndices = pages.map((_, i) => i).filter(i => i % 2 === 1); // even pages
      }

      for (let i = 0; i < targetIndices.length; i++) {
        const page = pages[targetIndices[i]];
        const mb = page.getMediaBox();
        const { x, y, w, h } = cropRectToPdfBox(cropRect, s, mb);
        page.setMediaBox(x, y, w, h);
        page.setCropBox(x, y, w, h);
        setProgress((i + 1) / targetIndices.length);
      }

      const bytes = await pdfLibDoc.save();
      const blob = new Blob([bytes], { type: 'application/pdf' });
      if (usageCapApplies()) incrementUsage();
      setResult({ blob, originalSize: fileSize, croppedSize: blob.size });
    } catch (err) {
      setError(`Crop failed: ${err.message}`);
    } finally {
      setProcessing(false);
    }
  }, [arrayBuffer, cropRect, pageSize, pageMode, currentPage, customRange, numPages, fileSize]);

  // ── overlay canvas effect ──
  useEffect(() => {
    const canvas = overlayCanvasRef.current;
    if (!canvas || !cropRect || !pageSize) return;
    const dpr = window.devicePixelRatio || 1;
    const newW = Math.round(pageDisplayW * dpr);
    const newH = Math.round(pageDisplayH * dpr);
    // Only resize the canvas buffer when page dimensions change — avoids clearing on every drag frame
    if (canvas.width !== newW || canvas.height !== newH) {
      canvas.width  = newW;
      canvas.height = newH;
      canvas.style.width  = `${pageDisplayW}px`;
      canvas.style.height = `${pageDisplayH}px`;
    }
    drawOverlay(canvas, cropRect, pageDisplayW, pageDisplayH, dpr);
  }, [cropRect, pageSize, scale, pageDisplayW, pageDisplayH]);

  const handleMouseDown = useCallback((e) => {
    if (!cropRect || !pageSize) return;
    setAutoMsg('');
    setEqualMargin('');
    const { offsetX: mx, offsetY: my } = e.nativeEvent;
    const handle = getHandleAt(mx, my, cropRect);

    // When crop rect is nearly full-page, clicking inside should start a new rect
    // (otherwise the user can't do anything — move is useless on a full-page rect)
    const isFullPage = cropRect.x < 2 && cropRect.y < 2 &&
      Math.abs(cropRect.w - pageDisplayW) < 2 && Math.abs(cropRect.h - pageDisplayH) < 2;

    if (handle) {
      dragRef.current = { mode: 'resize', handle, startX: mx, startY: my, startRect: { ...cropRect } };
    } else if (!isFullPage && mx >= cropRect.x && mx <= cropRect.x + cropRect.w && my >= cropRect.y && my <= cropRect.y + cropRect.h) {
      dragRef.current = { mode: 'move', startX: mx, startY: my, startRect: { ...cropRect } };
    } else {
      dragRef.current = { mode: 'new', startX: mx, startY: my, startRect: { ...cropRect } };
    }
  }, [cropRect, pageSize, pageDisplayW, pageDisplayH]);

  const handleMouseMove = useCallback((e) => {
    const canvas = overlayCanvasRef.current;
    if (!canvas || !cropRect) return;
    const { offsetX: mx, offsetY: my } = e.nativeEvent;
    const maxW = pageDisplayW;
    const maxH = pageDisplayH;

    // Update cursor
    canvas.style.cursor = getCursorAt(mx, my, cropRect);

    const drag = dragRef.current;
    if (!drag) return;

    const dx = mx - drag.startX;
    const dy = my - drag.startY;
    const sr = drag.startRect;

    if (drag.mode === 'move') {
      setCropRect(clampRect({ x: sr.x + dx, y: sr.y + dy, w: sr.w, h: sr.h }, maxW, maxH));
    } else if (drag.mode === 'resize') {
      setCropRect(applyResize(drag.handle, sr, dx, dy, maxW, maxH));
    } else if (drag.mode === 'new') {
      const x = Math.max(0, Math.min(drag.startX, mx));
      const y = Math.max(0, Math.min(drag.startY, my));
      const w = Math.min(Math.abs(mx - drag.startX), maxW - x);
      const h = Math.min(Math.abs(my - drag.startY), maxH - y);
      if (w > 0 && h > 0) setCropRect({ x, y, w, h });
    }
  }, [cropRect, pageDisplayW, pageDisplayH]);

  const handleMouseUp = useCallback((e) => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (!drag) return;
    // If new-rect drag was too small, restore previous rect
    if (drag.mode === 'new') {
      setCropRect(prev => (prev && (prev.w < MIN_RECT || prev.h < MIN_RECT)) ? drag.startRect : prev);
    }
  }, []);

  const handleMarginChange = useCallback((side, val) => {
    if (!pageSize) return;
    const pt = unitToPt(val, unit);
    const maxW = pageDisplayW;
    const maxH = pageDisplayH;
    setCropRect(prev => {
      if (!prev) return prev;
      let { x, y, w, h } = prev;
      const pxVal = pt * scaleRef.current;
      if (side === 'left')   { const newX = Math.max(0, pxVal); w = Math.max(MIN_RECT, w - (newX - x)); x = newX; }
      if (side === 'right')  { w = Math.max(MIN_RECT, maxW - x - Math.max(0, pxVal)); }
      if (side === 'top')    { const newY = Math.max(0, pxVal); h = Math.max(MIN_RECT, h - (newY - y)); y = newY; }
      if (side === 'bottom') { h = Math.max(MIN_RECT, maxH - y - Math.max(0, pxVal)); }
      return clampRect({ x, y, w, h }, maxW, maxH);
    });
  }, [pageSize, unit, pageDisplayW, pageDisplayH]);

  // ── render ──
  if (result) {
    return (<ProGate feature="crop"><ResultScreen result={result} fileName={fileName} pageSize={pageSize} cropRect={cropRect} scale={scale} onBack={() => setResult(null)} navigate={navigate} /></ProGate>);
  }

  return (
    <ProGate feature="crop">
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-4">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Crop PDF</h1>
          <p className="text-sm text-gray-500">Trim page margins permanently</p>
        </div>
      </div>

      {!pdfDoc ? (
        /* Upload zone */
        <div className="flex flex-col items-center justify-center min-h-[calc(100vh-73px)] p-8">
          <div
            onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}
            onClick={handleBrowse}
            className={`w-full max-w-xl border-2 border-dashed rounded-2xl p-16 text-center cursor-pointer transition-all ${
              isDragging ? 'border-brand-blue bg-brand-light/30 scale-[1.02]' : 'border-gray-300 hover:border-brand-blue hover:bg-gray-50'
            }`}
          >
            <Upload size={40} className={`mx-auto mb-4 ${isDragging ? 'text-brand-blue' : 'text-gray-400'}`} />
            <p className="text-lg font-medium text-gray-700">Drop a PDF here, or click to browse</p>
            <p className="text-sm text-gray-400 mt-1">PDF files only</p>
          </div>
          <CloudSourceHint
            onPicked={(r) => loadFile(new File([r.buffer], r.name, { type: 'application/pdf' }))}
            className="mt-4 w-full max-w-xl"
          />
          {error && <p role="alert" className="mt-4 text-red-600 text-sm">{error}</p>}
        </div>
      ) : (
        /* Two-column editor */
        <div className="flex h-[calc(100vh-73px)]">
          <div className="w-80 bg-white border-r border-gray-200 p-4 overflow-y-auto flex-shrink-0 flex flex-col gap-5">

            {/* File info */}
            <div className="text-sm text-gray-600">
              <p className="font-medium text-gray-800 truncate">{fileName}</p>
              <p className="text-gray-400">{numPages} page{numPages !== 1 ? 's' : ''} · {formatFileSize(fileSize)}</p>
            </div>

            {/* Unit toggle */}
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Units</p>
              <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                {['pt', 'in', 'mm'].map(u => (
                  <button key={u} onClick={() => setUnit(u)}
                    className={`flex-1 py-1.5 text-sm font-medium transition-colors ${unit === u ? 'bg-brand-blue text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
                    {u}
                  </button>
                ))}
              </div>
            </div>

            {/* Margin inputs */}
            {cropRect && pageSize && (
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Margins to remove</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { label: 'Top',    side: 'top',    val: cropRect.y / scaleRef.current },
                    { label: 'Bottom', side: 'bottom', val: (pageSize.height * scaleRef.current - cropRect.y - cropRect.h) / scaleRef.current },
                    { label: 'Left',   side: 'left',   val: cropRect.x / scaleRef.current },
                    { label: 'Right',  side: 'right',  val: (pageSize.width * scaleRef.current - cropRect.x - cropRect.w) / scaleRef.current },
                  ].map(({ label, side, val }) => (
                    <div key={side}>
                      <label className="text-xs text-gray-500 mb-1 block">{label}</label>
                      <input type="number" min="0" step={unit === 'pt' ? '1' : '0.01'}
                        value={ptToUnit(val, unit)}
                        onChange={e => handleMarginChange(side, e.target.value)}
                        className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
                      />
                    </div>
                  ))}
                </div>
                <button onClick={() => setCropRect({ x: 0, y: 0, w: pageSize.width * scaleRef.current, h: pageSize.height * scaleRef.current })}
                  className="mt-2 flex items-center gap-1 text-xs text-gray-500 hover:text-brand-blue transition-colors">
                  <Maximize2 size={12} /> Reset to full page
                </button>
              </div>
            )}

            {/* Presets */}
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Presets</p>
              <div className="flex flex-col gap-2">
                <button onClick={autoDetectMargins} disabled={autoDetecting}
                  className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 rounded-lg hover:border-brand-blue hover:text-brand-blue transition-colors disabled:opacity-50">
                  {autoDetecting ? <Loader2 size={14} className="animate-spin" /> : <CropIcon size={14} />}
                  Auto-detect margins
                </button>
                {autoMsg && <p className="text-xs text-amber-600">{autoMsg}</p>}

                <div className="flex items-center gap-2">
                  <input type="number" min="0" placeholder="Equal margins…" value={equalMargin}
                    onChange={e => {
                      setEqualMargin(e.target.value);
                      if (!pageSize || e.target.value === '') return;
                      const pt = unitToPt(e.target.value, unit);
                      const s = scaleRef.current;
                      const px = pt * s;
                      const maxW = pageSize.width  * s;
                      const maxH = pageSize.height * s;
                      setCropRect(clampRect({ x: px, y: px, w: maxW - px * 2, h: maxH - px * 2 }, maxW, maxH));
                    }}
                    className="flex-1 border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
                  />
                </div>

                <div className="grid grid-cols-2 gap-1">
                  {Object.keys(PAPER_SIZES_PT).map(name => (
                    <button key={name} onClick={() => setPaperSize(name)}
                      className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg hover:border-brand-blue hover:text-brand-blue transition-colors text-gray-700">
                      {name}
                    </button>
                  ))}
                </div>
                {paperMsg && <p className="text-xs text-red-500">{paperMsg}</p>}
              </div>
            </div>

            {/* Page scope */}
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Apply to</p>
              <div className="flex flex-col gap-1.5">
                {[
                  { id: 'current', label: `Current page (${currentPage})` },
                  { id: 'all',     label: 'All pages' },
                  { id: 'custom',  label: 'Custom range' },
                  { id: 'odd',     label: 'Odd pages' },
                  { id: 'even',    label: 'Even pages' },
                ].map(({ id, label }) => (
                  <label key={id} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input type="radio" name="pageMode" value={id} checked={pageMode === id}
                      onChange={() => setPageMode(id)} className="accent-brand-blue" />
                    {label}
                  </label>
                ))}
              </div>
              {pageMode === 'custom' && (
                <input type="text" placeholder='e.g. 1-3, 5, 8' value={customRange}
                  onChange={e => setCustomRange(e.target.value)}
                  className="mt-2 w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
                />
              )}
              {numPages > 1 && pageMode === 'all' && (
                <p className="mt-1.5 text-xs text-gray-400">Crop applied in points — results may vary on pages of different sizes.</p>
              )}
            </div>

            {/* Action */}
            <div className="mt-auto pt-2">
              {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
              {processing ? (
                <div>
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                    <Loader2 size={16} className="animate-spin" /> Cropping…
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-brand-blue rounded-full transition-all" style={{ width: `${progress * 100}%` }} />
                  </div>
                </div>
              ) : (
                <button onClick={cropPDF} disabled={!cropRect}
                  className="w-full flex items-center justify-center gap-2 bg-brand-blue text-white py-3 rounded-xl hover:bg-brand-blue/90 transition-colors font-medium disabled:opacity-40">
                  <CropIcon size={18} /> Crop PDF
                </button>
              )}
            </div>

          </div>

          {/* Editor area */}
          <div className="flex-1 flex flex-col items-center justify-center p-6 overflow-auto">
            {cropRect && pageSize && (
              <>
                {numPages > 1 && (
                  <div className="flex items-center gap-2 mb-3">
                    <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage <= 1}
                      className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 transition-colors">
                      <ChevronLeft size={18} />
                    </button>
                    <input
                      type="number" min="1" max={numPages}
                      value={pageInputVal}
                      onChange={e => setPageInputVal(e.target.value)}
                      onBlur={e => goToPage(parseInt(e.target.value, 10) || currentPage)}
                      onKeyDown={e => { if (e.key === 'Enter') goToPage(parseInt(e.target.value, 10) || currentPage); }}
                      className="w-14 text-center border border-gray-200 rounded-lg py-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
                    />
                    <span className="text-sm text-gray-500">of {numPages}</span>
                    <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage >= numPages}
                      className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 transition-colors">
                      <ChevronRight size={18} />
                    </button>
                  </div>
                )}

                {/* Live dimensions */}
                <p className="text-xs text-gray-500 mb-3">
                  Output: <strong>{(cropRect.w / scale).toFixed(0)} × {(cropRect.h / scale).toFixed(0)} pt</strong>
                  {' '}({(cropRect.w / scale / 72).toFixed(2)} × {(cropRect.h / scale / 72).toFixed(2)} in)
                  {'  ·  '}
                  Removing — T: {(cropRect.y / scale).toFixed(0)}pt  B: {((pageSize.height * scale - cropRect.y - cropRect.h) / scale).toFixed(0)}pt  L: {(cropRect.x / scale).toFixed(0)}pt  R: {((pageSize.width * scale - cropRect.x - cropRect.w) / scale).toFixed(0)}pt
                </p>

                {/* Hint when crop is full page */}
                {cropRect.x < 2 && cropRect.y < 2 &&
                  Math.abs(cropRect.w - pageDisplayW) < 2 && Math.abs(cropRect.h - pageDisplayH) < 2 && (
                  <p className="text-xs text-gray-400 mb-2">Click and drag on the page to define the crop area</p>
                )}

                {/* Stacked canvases */}
                <div className="relative inline-block shadow-xl rounded">
                  <PageCanvas pdfDoc={pdfDoc} pageNumber={currentPage} scale={scale} />
                  <canvas
                    ref={overlayCanvasRef}
                    className="absolute inset-0"
                    style={{ cursor: 'crosshair' }}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}
      <FreeTierBanner open={showFreeTierModal} onClose={() => setShowFreeTierModal(false)} />
    </div>
    </ProGate>
  );
}

function ResultScreen({ result, fileName, pageSize, cropRect, scale, onBack, navigate }) {
  const croppedW = cropRect ? cropRect.w / scale : 0;
  const croppedH = cropRect ? cropRect.h / scale : 0;

  const download = () => {
    const url = URL.createObjectURL(result.blob);
    const a = document.createElement('a');
    a.href = url; a.download = `cropped-${fileName}`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-8">
      <div className="bg-white rounded-2xl border border-gray-200 p-8 max-w-md w-full text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CropIcon size={28} className="text-green-600" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-1">Crop complete</h2>
        <p className="text-sm text-gray-500 mb-6">
          {pageSize && `Original: ${pageSize.width.toFixed(0)} × ${pageSize.height.toFixed(0)} pt`}
          <br />
          {`Cropped: ${croppedW.toFixed(0)} × ${croppedH.toFixed(0)} pt`}
          <br />
          {`${formatFileSize(result.originalSize)} → ${formatFileSize(result.croppedSize)}`}
        </p>
        <div className="flex flex-col gap-3">
          <button onClick={download} className="flex items-center justify-center gap-2 w-full bg-brand-blue text-white py-3 rounded-xl hover:bg-brand-blue/90 transition-colors font-medium">
            <Download size={18} /> Download cropped PDF
          </button>
          <button
            onClick={() => navigate('/viewer', { state: { file: result.blob } })}
            className="flex items-center justify-center gap-2 w-full bg-gray-100 text-gray-700 py-3 rounded-xl hover:bg-gray-200 transition-colors font-medium"
          >
            <Eye size={18} /> Open in Viewer
          </button>
          <button onClick={onBack} className="text-sm text-gray-500 hover:text-gray-700 transition-colors">
            ← Crop another file
          </button>
        </div>
      </div>
    </div>
  );
}
